import React, { useState } from 'react';
import Tooltip from '@mui/material/Tooltip';

import {
  GridCellProps,
  GridColumn as Column,
} from '@progress/kendo-react-grid';
import { LightPageHeader } from 'Components/Display/PageHeaders';
import { AddButton } from 'Components/Form';
import {
  EditDeleteCell,
  FilterBarMainGrid,
  GridButtonSection,
  LinkCell,
  useEditDeleteCell,
} from 'Components/Grid';
import { TextDeleteButton, TextEditButton } from 'Components/Form';
import { SecureComponent } from 'Components/Security';
import { FormProvider, GridProvider } from 'Context';
import { FeatureFlags, isFeatureEnabled } from 'Utilities/FeatureFlagProvider';
import { ReviewerProfileTab } from 'v3/Components/Pages/ReviewerProfile/ReviewerProfile.Header';
import {
  defaultGridProps,
  FilterBarFilterType,
  FilterConfig,
  IFilterConfig,
} from 'Models';
import { Permission } from 'Models/Templates/Permission/Permission';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { addEditRoute, AddEditSubRoute, ROUTES } from 'Navigation';
import { Link } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { buildRoute, useGridSearch, useHasPermissions } from 'Utilities';
import { PrimaryButton, SecondaryButton } from 'v3/Components/Shared/Buttons';
import { Close, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

import { Dialog } from '@progress/kendo-react-dialogs';
import { Render } from 'Components/Display';
import { InputActive } from 'v3/Components/Form/InputActive';
import { FieldError } from 'v3/Components/Form/FormSection';

const NewReviewerButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };
  const [ShowModal, setShowModal] = useState(false);
  const handleModalDismiss = () => {
    setShowModal(false);
  };
  const NewReviewerDropdown = () => {
    return (
      <div className="buttonWrapper">
        {/* if variant is white then use secondary button */}
        {(
          <PrimaryButton
            onClick={toggleDropdown}
            className={`${isOpen ? 'open' : ''}`}
          >
            <div className="title">{'Add Reviewer'}</div>

            <span className='arrowIcon' style={{ padding: 0 }}>
              {!isOpen ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
            </span>
          </PrimaryButton>
        )}
        {isOpen && (
          <div>
            <ul className="newReferralList">

              <li
                key={"Peer Review"}
                onClick={() => {
                  setShowModal(true);
                }
                }
              >
                <span className="linkText">
                  Peer Review
                </span>
              </li>
              <li
                key={"Peer Review - IME"}
                onClick={() => {
                  setShowModal(true);
                }
                }
              >
                <span className="linkText">
                  Peer Review, IME
                </span>
              </li>
              <li
                key={"IME"}
                onClick={() => {
                  setShowModal(true);
                }
                }
              >
                <span className="linkText">
                  IME
                </span>
              </li>
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <NewReviewerDropdown />
      <Render condition={ShowModal}>
        <AddReviewerDialog isOpen={ShowModal} toggle={handleModalDismiss} />
      </Render>
    </>
  );
};

export enum ReviewerGridIds {
  DetailedSearch = 'detailedSearch',
}

export const Reviewers: React.FC = () => {
  const entityName = 'Reviewer';

  return (
    <GridProvider entityName={entityName}>
      <Breadcrumb tag="nav" listTag="div">
        <BreadcrumbItem>
          <Link to={ROUTES.DASHBOARD}>Home</Link>
        </BreadcrumbItem>
        <BreadcrumbItem>Reviewer Panel</BreadcrumbItem>
      </Breadcrumb>
      <LightPageHeader tall noBorder className="page-header-shadow header-wrapper">
        <h1>Reviewer Panel</h1>
        {isFeatureEnabled(FeatureFlags.V3ReviewerProfile) &&
          (
            <NewReviewerButton />
          )
        }
      </LightPageHeader>
      <FilterBarDetailedSearchGrid />
    </GridProvider>
  );
};

class ReviewerSearch {
  searchKey?: string;
}

export const FilterBarDetailedSearchGrid: React.FC = () => {
  const reviewerSearch = new ReviewerSearch();

  return (
    <FormProvider formModel={reviewerSearch}>
      <ReviewersGrid />
    </FormProvider>
  );
};

const ReviewersGrid: React.FC = () => {
  const [gridToggler] = useGridSearch();
  const filters: IFilterConfig[] = [
    new FilterConfig('searchKey', FilterBarFilterType.SearchText, ' Name'),
  ];
  return (
    <React.Fragment>
      <SecureComponent permissions={[Permission.AddReviewer]}>
        <GridButtonSection>
          <AddButton url={addEditRoute(AddEditSubRoute.Reviewer)}>
            New Reviewer
          </AddButton>
        </GridButtonSection>
      </SecureComponent>
      <FilterBarMainGrid
        isMainGrid
        filterBarProps={{
          filterConfigurations: filters,
          filterValues: [],
          isOpen: true,
        }}
        refreshToggle={gridToggler}
        readEndpoint={ENDPOINTS.reviewers.read}
        {...defaultGridProps}
      >
        <Column field="name" title="Name" />
        <Column field="email" title="Email" />
        <Column field="note" title="Notes" />
        <Column field="paCaseId" title="PA Case Id" />
        <Column field="contactNumber" title="Contact Number" />
        <Column field="converted" title="User Status" cell={ConvertCell} />
        <Column
          field="id"
          width="100"
          title="Actions"
          cell={ActionsCell}
          className="flex-end"
          sortable={false}
        />
      </FilterBarMainGrid>
    </React.Fragment>
  );
};

const convertText = (text: string) => 'Convert';

const ConvertCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  const converted = props.dataItem?.converted;
  const hasEmail = props.dataItem?.email ? true : false;
  const tooltipText = 'A Reviewer Email is requiered';
  const hasPermissions = useHasPermissions();

  let content;

  if (converted) {
    content = <td>Converted</td>;
  } else if (hasEmail) {
    content = (
      <LinkCell
        {...props}
        endpoint={buildRoute(ROUTES.REVIEWER_CONVERT)}
        linkField="id"
        field="converted"
        convertFunction={convertText}
      />
    );
  } else {
    content = (
      <td>
        <Tooltip title={tooltipText}>
          <Link to={'#'}>Convert</Link>
        </Tooltip>
      </td>
    );
  }
  if (!hasPermissions([Permission.ConvertReviewer]) && !converted) {
    content = <td>Not Converted</td>;
  }
  return content;
};
const ActionsCell: React.FC<GridCellProps> = (props) =>
  isFeatureEnabled(FeatureFlags.V3ReviewerProfile) ? (
    <ReviewerProfileActionsCell {...props} />
  ) : (
    <EditDeleteCell
      editRoute={addEditRoute(AddEditSubRoute.Reviewer)}
      editPermissions={[Permission.EditReviewer]}
      deleteEndpoint={ENDPOINTS.reviewers.base}
      deletePermissions={[Permission.DeleteReviewer]}
      {...props}
    />
  );

const ReviewerProfileActionsCell: React.FC<GridCellProps> = (props) => {
  const { id } = props.dataItem;
  const { deleteItem } = useEditDeleteCell({ deleteEndpoint: ENDPOINTS.reviewers.base, ...props });

  return (
    <td>
      <div className="flex-vertical-center flex-gap-small flex-end full-height my-auto">
        <SecureComponent permissions={[Permission.EditReviewer]}>
          <TextEditButton url={`${ROUTES.REVIEWER_PROFILE}/${id}/${ReviewerProfileTab.Overview}`} />
        </SecureComponent>
        <SecureComponent permissions={[Permission.DeleteReviewer]}>
          <TextDeleteButton onClick={deleteItem} />
        </SecureComponent>
      </div>
    </td>
  );
};

interface AddReviewerModalProps {
  isOpen: boolean;
  toggle: () => void;
}

const AddReviewerDialog: React.FC<AddReviewerModalProps> = (
  {
    isOpen,
    toggle,
  }
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dialogTitle = 'Add Reviewer';
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      //checking if fisrt and last name are filled out, if not, return early and show error message
      if (!firstName || !lastName) {
        //show error message
        setFieldErrors([
          { fieldId: 'firstName', error: !firstName ? 'First name is required' : '' },
          { fieldId: 'lastName', error: !lastName ? 'Last name is required' : '' }
        ]);
        return;
      }
      else {
        //validating value just contains letters, spaces and hyphens
        const nameRegex = /^[a-zA-Z\s-]+$/;
        if (!nameRegex.test(firstName) || !nameRegex.test(lastName)) {
          setFieldErrors([
            { fieldId: 'firstName', error: !nameRegex.test(firstName) ? 'First name can only contain letters, spaces, and hyphens' : '' },
            { fieldId: 'lastName', error: !nameRegex.test(lastName) ? 'Last name can only contain letters, spaces, and hyphens' : '' }
          ]);
          return;
        }
      }
      //redirect to AddEdit/Reviewer
      window.location.href = buildRoute(ROUTES.ADD_EDIT, AddEditSubRoute.Reviewer + `?firstName=${encodeURIComponent(firstName)}&lastName=${encodeURIComponent(lastName)}`);
    } catch (error) {
      console.error('Error claiming ownership:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  //use effect to clear field errors when first name or last name changes
  React.useEffect(() => {
    setFieldErrors(prevErrors => prevErrors.filter(fe => fe.fieldId !== 'firstName' && fe.fieldId !== 'lastName'));
  }, [firstName, lastName]);

  // use effect to show alert if there are field errors
  React.useEffect(() => {
    if (fieldErrors.length > 0) {
      console.log('Field errors:', fieldErrors);
    }
  }, [fieldErrors]);

  const handleDecline = () => {
    //closing dialog
    toggle();
  };

  if (!isOpen) return null;

  return (
    <Dialog className="referral-ownership-dialog" onClose={handleDecline}>
      <div className="dialog-header">
        <div className="dialog-title">{dialogTitle}</div>
        <div className="dialog-close" onClick={handleDecline}>
          <span className="dialog-close-icon">
            <Close />
          </span>
        </div>
      </div>
      <div className="dialog-row">
        <div className="dialog-column">
          <p>
            Please provide the name of the new reviewer:
          </p>
          <div className="dialog-row">
            <div className="dialog-column">
              <InputActive
                id="firstName"
                name="firstName"
                label="First Name"
                value={firstName}
                onChange={(e: any) => setFirstName(e.target.value)}
                required
                style={{
                  width: '80%'
                }}
                error={fieldErrors
                  ?.find(f => f.fieldId === 'firstName')
                  ?.error
                }
              />
            </div>
            <div className="dialog-column">
              <InputActive
                id="lastName"
                name="lastName"
                label="Last Name"
                value={lastName}
                onChange={(e: any) => setLastName(e.target.value)}
                required
                style={{
                  width: '80%'
                }}
                error={fieldErrors
                  ?.find(f => f.fieldId === 'lastName')
                  ?.error
                }
              />
            </div>
          </div>
        </div>
      </div>
      <div className="dialog-footer">
        <SecondaryButton
          className="centered"
          onClick={handleDecline}
          disabled={isSubmitting}
        >
          <div className="title">Cancel</div>
        </SecondaryButton>
        <PrimaryButton
          className={`centered ${isSubmitting ? 'disabled' : ''}`}
          onClick={handleConfirm}
          disabled={isSubmitting}
        >
          <div className="title">
            {isSubmitting ? 'Adding...' : 'Add'}
          </div>
        </PrimaryButton>
      </div>
    </Dialog>
  );
}